import * as React from 'react';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import Popover from 'antd/lib/popover';
import Button from 'antd/lib/button';
import { useSelector } from 'react-redux';
import { FileTextOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
    ArchetypeSpecificStepRunSummaryPanelProps,
    StepRunSummaryContainer,
    StepRunSummaryDetailItem,
    StepRunSummaryDetailSeparator,
    CIBuildInfoTopBar,
    CIBuildInfoBranchItem,
    CIBuildInfoCommitItem,
    CIBuildInfoMessageItem,
} from '.';
import { BuildArtifact, ReleaseStepReleaseCandidateData } from '@/types';
import { bytesToMB } from '@/helpers';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { BinaryBuildDetailsView } from '@/components/workflowModule/BinaryBuildDetailsView';
import { ArtifactInstallMenu } from '@/components/workflowModule/ArtifactsModule';
import {
    selectOrg,
    selectAppDistributionServices,
    selectAppIdentifiers,
    selectStabilityMonitoringIntegration,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { StabilityMonitoringService } from '@/types';

export const ReleaseCandidateStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({
    stepRun,
    appId,
    shouldDisplayCompactVersion,
}) => {
    const org = useSelector((state: RootState) => selectOrg(state));
    const orgId = org?.orgId;
    const distributionServices = useSelector((state: RootState) => selectAppDistributionServices(state, appId));
    const appIdentifiers = useSelector((state: RootState) => selectAppIdentifiers(state, appId));
    const stabilityMonitoring = useSelector((state: RootState) =>
        selectStabilityMonitoringIntegration(state, appId),
    ) as StabilityMonitoringService;

    const stepData: ReleaseStepReleaseCandidateData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    };

    const builds = stepData.builds ?? [];
    const selectedBuild = builds.find((build) => build.isSelectedBuild);
    const ciBuild = selectedBuild?.ciBuild;
    const workflowData = stepData.workflowData;

    const artifacts = ciBuild?.artifacts ?? [];

    const topBar = (
        <CIBuildInfoTopBar
            ciBuild={ciBuild}
            workflowData={workflowData}
            workflowTooltip="The workflow that generates your RC build."
        />
    );

    const formatArtifactsBody = () => {
        if (artifacts.length === 0) {
            return <Text style={{ color: antGrey.grey7, fontStyle: 'italic' }}>No artifacts</Text>;
        }

        return (
            <Space
                direction="vertical"
                size={0}
                split={<Divider style={{ margin: '8px 0', backgroundColor: antGrey.grey4 }} />}
                style={{ width: '100%' }}
            >
                {artifacts.map((artifact: BuildArtifact, index: number) => {
                    const hasDetails =
                        artifact.binaryBuildDetails ||
                        (artifact.appStoreInfos && Object.keys(artifact.appStoreInfos).length > 0) ||
                        artifact.dsymUploadInfo;

                    const sizeInMB = bytesToMB(artifact.size);

                    return (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Space align="center" size={4}>
                                <FileTextOutlined style={{ color: antGrey.grey7 }} />
                                <Button
                                    type="link"
                                    style={{ padding: 0, fontWeight: 600 }}
                                    onClick={() => {
                                        if (artifact.downloadUrl) {
                                            window.open(artifact.downloadUrl, '_self');
                                        }
                                    }}
                                >
                                    {artifact.fileName}
                                </Button>
                                {sizeInMB > 0 && <Text type="secondary">({sizeInMB} MB)</Text>}
                                {hasDetails && (
                                    <Popover
                                        content={
                                            <BinaryBuildDetailsView
                                                artifact={artifact}
                                                showUploadStatus
                                                distributionServices={distributionServices}
                                                appIdentifiers={appIdentifiers}
                                                stabilityMonitoring={stabilityMonitoring}
                                            />
                                        }
                                        trigger="hover"
                                        placement="bottom"
                                    >
                                        <InfoCircleOutlined
                                            style={{ color: runwayColors.info, cursor: 'pointer', marginLeft: 4 }}
                                        />
                                    </Popover>
                                )}
                            </Space>
                            {artifact.binaryBuildDetails && orgId && (
                                <ArtifactInstallMenu orgId={orgId} appId={appId} artifact={artifact} />
                            )}
                        </div>
                    );
                })}
            </Space>
        );
    };

    return (
        <StepRunSummaryContainer topBar={topBar}>
            {!shouldDisplayCompactVersion && (
                <>
                    <StepRunSummaryDetailItem
                        header="BRANCH"
                        body={<CIBuildInfoBranchItem ciBuild={ciBuild} workflowData={workflowData} />}
                    />
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="COMMIT"
                        body={<CIBuildInfoCommitItem ciBuild={ciBuild} workflowData={workflowData} />}
                    />
                    <StepRunSummaryDetailSeparator />
                    <StepRunSummaryDetailItem
                        header="MESSAGE"
                        body={<CIBuildInfoMessageItem ciBuild={ciBuild} workflowData={workflowData} />}
                    />
                    {artifacts.length > 0 && (
                        <>
                            <div style={{ flexBasis: '100%', height: 0 }} />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    padding: '12px 20px',
                                    width: '100%',
                                    flex: 1,
                                    borderTop: `1px solid ${antGrey.grey4}`,
                                }}
                            >
                                <Text
                                    style={{
                                        ...titleStyle,
                                        color: antGrey.grey7,
                                        fontSize: 10,
                                    }}
                                >
                                    ARTIFACTS
                                </Text>
                                {formatArtifactsBody()}
                            </div>
                        </>
                    )}
                </>
            )}
        </StepRunSummaryContainer>
    );
};
